import { useQuery } from '@tanstack/react-query';
import {
  confidenceBand,
  type AgentRun,
  type AgentTask,
  type Engagement,
  type FindingSuggestion,
} from '@ai-secure-twin/shared';
import { api } from '../../lib/api';

/**
 * The agent's suggestions, as a person sees them before deciding.
 *
 * Everything here is advice. The score is the agent's own confidence against a fixed rubric —
 * not a measured probability and not a severity — and it never moves, approves or records
 * anything. A person reads it, weighs it, and decides.
 */

const isPending = (run: AgentRun) => run.status === 'QUEUED' || run.status === 'RUNNING';

/** Every run for an engagement, polled while one is in flight. Shared by every panel. */
export function useAgentRuns(engagementId: string) {
  return useQuery({
    queryKey: ['agentRuns', engagementId],
    queryFn: () => api.listAgentRuns(engagementId),
    refetchInterval: (query) => ((query.state.data?.items ?? []).some(isPending) ? 3000 : false),
  });
}

/** The newest successful run of any of these tasks. */
export function latestRun(
  runs: readonly AgentRun[] | undefined,
  tasks: readonly AgentTask[],
): AgentRun | undefined {
  return [...(runs ?? [])]
    .filter((r) => tasks.includes(r.task) && r.status === 'SUCCEEDED' && r.result)
    .sort((a, b) => b.requestedAt.localeCompare(a.requestedAt))[0];
}

/**
 * Whether advice was given about a state the engagement has since left. Stage changes bump
 * `updatedAt`, so a run requested before it was advising on something that no longer exists.
 */
export const isStale = (run: AgentRun, engagement: Pick<Engagement, 'updatedAt'>) =>
  run.requestedAt < engagement.updatedAt;

/** The suggestion a triage run made for one finding, if it made one. */
export function suggestionFor(
  run: AgentRun | undefined,
  findingId: string,
): FindingSuggestion | undefined {
  return run?.result?.findingSuggestions.find((s) => s.findingId === findingId);
}

/** "7/10 · Likely — verify", coloured by band. */
export function ScoreBadge({ score }: { score: number }) {
  const band = confidenceBand(score);
  return (
    <span
      className={`score score-${band.tone}`}
      title="The agent's own confidence in this suggestion, against a fixed rubric. Not a probability, not a severity."
    >
      <strong>{score}/10</strong> · {band.label}
    </span>
  );
}
